import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import useEventCallback from "../../hooks/useEventCallback";
import { 
  Archive, 
  Coffee, 
  ChevronDown, 
  Clock,
} from "lucide-react";
import { Resizable } from "re-resizable";
import { 
  updateTaskService, 
  getTasksByCategoryService,
} from "../../utils/taskService";
import {
  updateProjectService,
} from "../../utils/projectService";
import {
  getNewTasksByCategoryService,
} from "../../utils/taskCategoryService";
import {
  startProject,
  resetProjectCreation,
} from "../../reducers/projectReducer";
import { 
  saveTask,
  syncTasksFromDB,
} from "../../reducers/taskReducer";
import { formatEstimation } from "../../utils/formatter";
import ProjectCreation from "../Project/ProjectCreation";
import ChatSession from "../Chat/ChatSession";
import TaskTable from "../Task/TaskTable";
import PrinceGeoffrey from "../Chat/PrinceGeoffrey";

export default function TaskCategory() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getToken, userId } = useAuth();
  const params = useParams();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  // Detect whether we are viewing a project or a category
  const projectId = params.id || null;
  const categoryId = params.categoryId || null;
  const isCategoryView = categoryId !== null;

  const currentProject = useSelector((state) => {
    if (projectId) {
      return state.projectList.projects.find(
        (project) => project.id === projectId
      );
    }

    if (categoryId) {
      // Clear any previously‐loaded category first
      return state.taskCategoryList.projectCategories.find(
        (category) => category.slug === categoryId
      );
    }

    return state.projectList.currentProject;
  });

  const projectTasks = useSelector((state) => {
    if (projectId) {
      // filter by metaData.projectId
      return state.taskList.tasks.filter(
        (task) => task?.metaData?.projectId === projectId
      );
    }

    if (categoryId) {
      // filter by metaData.projectCategory and check if associated project is not archived
      return state.taskList.tasks.filter(task => {
        // Make sure the task is not archived
        if (task.archived) return false;
        
        // First check if task belongs to category
        const belongsToCategory = task?.metaData?.projectCategory?.some(
          category => category.categoryId === categoryId
        );
        
        if (!belongsToCategory) return false;
        
        // Then check if task's project is not archived
        const projectId = task?.metaData?.projectId;
        if (!projectId) return true; // Keep tasks without project
        
        const associatedProject = state.projectList.projects.find(
          project => project.id === projectId
        );
        
        return !associatedProject?.archived;
      });
    }

    return [];
  });

  const { step } = currentProject || {};
  const [projectLoading, setProjectLoading] = useState(false);
  const [messageLoading] = useState(false);
  const [newMessages] = useState([]);
  const [isTaskRegenerateDisabled, setIsTaskRegenerateDisabled] = useState(false);
  const [isArchived] = useState(
    currentProject?.archived ?? false
  );
  const [isProjectDetailsOpen, setIsProjectDetailsOpen] = useState(true);
  const [projectRestart, setProjectRestart] = useState(false);
  const getNewTasksCalled = useRef(false);
  const getCategoryTaskCalled = useRef(false);
  const taskTableRef = useRef(null);

  // Resizable container - temporary location
  // TODO: This is a temporary solution. Need to move this to shareable component
  const [containerWidth, setContainerWidth] = useState(0);
  const [leftWidth, setLeftWidth] = useState(0);
  const containerRef = useRef(null);
  const MIN_LEFT_WIDTH = 300; // Minimum width for left panel in pixels
  const MIN_LEFT_PERCENTAGE = 0.25; // Minimum percentage of total width
  const DEFAULT_LEFT_RATIO = .4; // Default left panel takes 2/5 of the screen

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    
    window.addEventListener('resize', handleResize)
    handleResize() // Initialize on mount
    
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const getIsMobileOnly = () => {
    return windowWidth < 768
  }

  const loadCategoryTasks = useEventCallback(() => getCategoryTasks());

  useEffect(() => {
    if (categoryId) {
      loadCategoryTasks();
      // dispatch(setCurrentProjectCategory(categoryId));
      setIsTaskRegenerateDisabled(false);
    }
  }, [categoryId, loadCategoryTasks]);

  // Resizable container - effects and handlers
  // TODO: This is a temporary solution. Need to move this to shareable component
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
        setLeftWidth(width * DEFAULT_LEFT_RATIO);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Handle resize events
  // TODO: This is a temporary solution. Need to move this to shareable component
  const handleResizeStop = (e, direction, ref, d) => {
    const newWidth = leftWidth + d.width;
    
    // Apply minimum width constraints
    if (newWidth >= MIN_LEFT_WIDTH && newWidth >= containerWidth * MIN_LEFT_PERCENTAGE) {
      setLeftWidth(newWidth);
    }
  };

  const toggleProjectDetails = () => {
    setIsProjectDetailsOpen(!isProjectDetailsOpen);
  }

  const newProject = () => {
    return step === "gathering_info" || step === "";
  };

  const handleCreateNewProject = () => {
    dispatch(resetProjectCreation());
    navigate("/project/new");
  };

  const handleStartProject = async () => {
    await updateProjectService(getToken, currentProject.id, {
      ...currentProject,
      step: "in_progress",
    });

    for (const task of projectTasks) {
      const updatedTask = { ...task, status: 'ready' }
      await updateTaskService(getToken, userId, updatedTask)
      dispatch(saveTask({ task: updatedTask }));
    }

    dispatch(startProject(currentProject));
  };

  const getNewProjectCategoryTasks = async () => {
    getNewTasksCalled.current = true;
    setProjectLoading(true);
    
    try {
      const categoryTasksResponse = await getNewTasksByCategoryService(categoryId);

      if (categoryTasksResponse.data.length > 0) {
        // Loop through the tasks and add them to the Redux store
        // This will show each item with a slight delay for the user to see
        for (const task of categoryTasksResponse.data) {
          setTimeout(() => {
            dispatch(syncTasksFromDB({ tasks: [task] }));
          }, 100);
        }
      }
      else {
        console.log('No new project category tasks found for category', categoryId);
      }
    }
    catch (error) {
      console.error('Error getting new tasks by category:', error);
    }
    finally {
      setProjectLoading(false);
      getNewTasksCalled.current = false;
    }
  }

  const getCategoryTasks = async () => {
    if (!categoryId || getCategoryTaskCalled.current) return;
    getCategoryTaskCalled.current = true;

    try {
      const categoryTasksResponse = await getTasksByCategoryService(categoryId);

      if (categoryTasksResponse.data?.tasks?.length > 0) {
        // Bulk‐add tasks into Redux so `projectTasks` selector can pick them up
        dispatch(syncTasksFromDB({ tasks: categoryTasksResponse.data.tasks }));
      }
      else {
        await getNewProjectCategoryTasks();
      }
    } catch (error) {
      console.error('Error getting tasks by category:', error);
    } finally {
      getCategoryTaskCalled.current = false;
    }
  };

  const handleRegenerateTasksClick = () => {
    getNewProjectCategoryTasks();
    setIsTaskRegenerateDisabled(true);
  }

  const estimatedTime = formatEstimation(
    projectTasks?.reduce(
      (total, task) => Number(total) + Number(task.estimation),
      0
    )
  );

  const RenderProjectDetails = () => {
    return (
      <>
        <div className="flex justify-between items-center py-2 border-b border-slate-200">
          <div>
            {currentProject?.title && (
              <>
                <h1 className="text-2xl font-semibold text-slate-800 leading-tight">
                  {currentProject?.title}
                </h1>
                {currentProject?.description && (
                  <p className="mt-1 text-sm text-slate-600 leading-tight line-clamp-2">
                    {currentProject.description}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col w-full h-fit">
          <section className="flex items-center gap-2">

            <div className={`flex flex-col w-full ${getIsMobileOnly() ? 'max-h-[55vh]' : 'max-h-[93vh]'}`}>
              <div
                className={`flex justify-between w-full items-center pt-3 ${
                  messageLoading || projectLoading ? "animate-pulse" : ""
                }`}
              >
                <PrinceGeoffrey size={windowWidth < 768 ? 70 : 120} />

                {windowWidth < 768 && (
                  <button
                    onClick={() => toggleProjectDetails()}
                    className="text-sm m-0 p-0 text-stone-700 font-semibold bg-transparent hover:bg-transparent"
                  >
                    <ChevronDown size={28} />
                  </button>
                )}
              </div>

              {isProjectDetailsOpen && (
                <>
                  {step === "creating_tasks" && (
                    <div className="flex w-full flex-col bg-stone-100 rounded-md p-5 border border-slate-200">
                      <div className="text-base text-gray-700 flex flex-col">
                        <h4 className="text-base mb-4">
                          Behold the tasks necessary for the completion of this
                          project
                        </h4>
                        <p className="mb-4">
                          Review these tasks at your leisure. Amend, accept, or
                          dismiss them as you see fit.
                        </p>
                        <p className="mb-4">
                          Click{" "}
                          <span className="font-semibold">Start Project</span>{" "}
                          when you are ready to begin.
                        </p>
                      </div>

                      <div className="flex flex-row justify-start">
                        <button
                          className="mt-6 ml-0 text-lg w-full font-semibold bg-slate-800 hover:bg-slate-700 hover:text-white text-white border-gray-200 border rounded-md relative"
                          onClick={() => handleStartProject()}
                        >
                          Start Project
                        </button>
                      </div>
                    </div>
                  )}

                  {!currentProject && (
                    <div className="flex flex-col w-full p-2 mb-20 bg-stone-100 rounded-md border border-stone-200">
                      <div className="flex flex-col items-center justify-center text-sm text-gray-600">
                        <h4 className="text-2xl font-semibold">Hello,</h4>
                        <p className="p-10 text-center">
                          It appears we've ventured into uncharted territory—no
                          project with this ID exists.
                        </p>
                        <button onClick={() => navigate('/')}>Go Home</button>
                      </div>

                      <div className="flex flex-row justify-start">
                        <button
                          className="mt-6 ml-0 text-lg w-full font-semibold text-white border-gray-200 border rounded-md relative"
                          onClick={handleCreateNewProject}
                        >
                          Create New Project
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Chat Section */}
                  {projectId && (step === 'in_progress' || step === 'restart_project') && projectTasks.length > 0 && (
                    <ChatSession
                      messageLoading={messageLoading}
                      newMessages={newMessages}
                      projectRestart={projectRestart}
                      setProjectRestart={setProjectRestart}
                    />
                  )}

                  {categoryId && (
                    <div className="bg-stone-50 rounded-xl p-4 mb-4 border-t border-slate-200">
                      <p className="text-sm text-gray-700">
                        You are viewing all tasks tagged with "<span className="font-medium">{categoryId}</span>."
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      </>      
    );
  }

  // If neither a project nor a category exists, show the "nothing found" message
  if (!currentProject) {
    return (
      <div className="">
        <div className="flex flex-col items-center justify-center bg-stone-100 border-b-2">
          <p className="p-10 text-center">
            Ah, it appears we've ventured into uncharted territory—no project
            with this ID exists.
          </p>
          <button onClick={() => navigate('/')}>Go Home</button>
        </div>
      </div>
    );
  }

  if (newProject()) {
    return (
      <div className="w-full">
        <div className="mx-auto max-w-6xl flex flex-row items-start">
          <div className="p-10">
            <PrinceGeoffrey size={150} />
          </div>

          <div className="pb-[80px] w-3/4 h-full py-4 overflow-y-auto space-y-4">
            <ProjectCreation />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef}
      className={`w-full flex ${getIsMobileOnly() ? 'flex-col' : 'flex-row'}`}
    >
      {getIsMobileOnly() ? (
        <div className={`overflow-y-auto px-4 ${isProjectDetailsOpen ? 'h-3/5' : 'h-fit'}`}>
          {/* Left side content */}
          <div className="flex flex-col justify-center items-center h-fit">
            {RenderProjectDetails()}
          </div>
        </div>
      ) : (
        <Resizable
          size={{ width: leftWidth || containerWidth * DEFAULT_LEFT_RATIO, height: '100%' }}
          minWidth={Math.max(MIN_LEFT_WIDTH, containerWidth * MIN_LEFT_PERCENTAGE)}
          maxWidth={containerWidth * 0.7}
          enable={{ right: true }}
          onResizeStop={handleResizeStop}
          handleComponent={{
            right: <div className="w-1 h-full hover:bg-stone-300 cursor-ew-resize absolute right-0 top-0 z-10 transition-colors duration-200" />
          }}
          handleStyles={{
            right: {
              right: '-1px'
            }
          }}
        >
          <div className="flex flex-col h-full px-4 overflow-y-auto">
            {RenderProjectDetails()}
          </div>
        </Resizable>
      )}

      <div className={`flex flex-col w-full ${getIsMobileOnly() ? 'px-4' : 'pr-4'} ${(getIsMobileOnly()) ? !isProjectDetailsOpen ? 'h-3/4' : 'h-2/5' : 'h-full'}`}> 
        <div className={`flex flex-col w-full h-full bg-white rounded-xl my-3 mx-0 px-4 shadow-xl border border-slate-200 transition-all duration-500 overflow-y-auto`}>
         
          <div className="flex items-start justify-between pt-2 pb-2">
            <div
              className={`flex flex-row items-center ${
                projectTasks?.length > 0 ? "" : "opacity-50"
              }`}
            >
              <p className="text-2xl font-bold">
                {projectTasks?.length} {categoryId ? 'Category' : 'Project'} Tasks
              </p>
            </div>

            <div className="flex items-center">
              <button
                disabled={isTaskRegenerateDisabled || projectLoading}
                className={`ml-4 rounded-lg font-semibold px-3 py-1 ${
                  isTaskRegenerateDisabled || projectLoading
                    ? 'bg-gray-200 text-gray-400 hover:bg-gray-200 hover:text-gray-400 cursor-not-allowed' 
                    : 'bg-sky-500 hover:bg-sky-600 hover:text-white text-white'
                }`}
                onClick={isTaskRegenerateDisabled || projectLoading ? undefined : handleRegenerateTasksClick}
              >
                Regenerate Tasks
              </button>
            </div>
          </div>

          {projectId && (step === "creating_tasks" || step === "in_progress") &&
            projectTasks?.length !== 0 && (
              <div className="flex flex-col relative w-full mx-auto p-0">
                <div className="pt-1 pb-2 flex text-md text-slate-500 font-semibold">
                  {isArchived ? (
                    <div className="flex items-center">
                      <Archive size={20} className="mr-2" />
                        Project Archived
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Clock size={20} className="mr-2" />
                        {estimatedTime} Estimated
                    </div>
                  )}
                </div>
              </div>
            )
          }

          <div className="relative min-h-[200px]">
            {projectLoading && (
              <div className="absolute inset-0 p-3 rounded-md bg-black bg-opacity-50 z-10">
                <div className="sticky top-36">
                  <div className="flex flex-row sticky items-center justify-center gap-2 bg-white py-8 rounded-lg shadow-lg text-center">
                    <div className="flex flex-row items-center animate-bounce text-amber-900 justify-start mt-1 mr-4">
                      <Coffee size={80} />
                    </div>
                    <div className="">
                      <h2 className="text-2xl text-left font-bold mb-4">
                        {projectId 
                          ? "Your project tasks will be ready in a moment." 
                          : "Your category tasks will be ready in a moment."}  
                      </h2>
                      <p className="text-left">
                        Grab a cup of coffee, I will inform you when I am
                        done.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className={projectLoading ? "pointer-events-none" : ""}>
              <TaskTable
                currentProject={currentProject}
                projectTasks={projectTasks}
                categoryId={categoryId}
                isCategoryView={isCategoryView}
                ref={taskTableRef}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
